import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sun, CheckCircle2, Loader2, Phone } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const TYPES = ["Panel","Inverter","Wiring","Monitoring","Structure","Other"];

export default function ComplaintRegister() {
  const [co, setCo] = useState(null);
  const [form, setForm] = useState({ name: "", mobile: "", complaint_type: "Panel", description: "", address: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/public/config").then(({ data }) => setCo(data)).catch(() => setErr("Service unavailable. Please try later."));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post(`/complaints/public/${co.token}`, form);
      setDone(data.complaint_no);
    } catch (ex) {
      setErr(formatError(ex.response?.data?.detail) || "Submission failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background:"linear-gradient(135deg,#0B132B 0%,#1C2541 100%)"}} data-testid="complaint-register-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-5 flex items-center gap-3">
            {co?.logo_url ? (
              <img src={co.logo_url} alt="MRM" className="h-11 w-11 object-contain bg-white rounded-lg p-1" data-testid="register-logo"/>
            ) : (
              <div className="w-11 h-11 bg-white/20 rounded-lg flex items-center justify-center"><Sun className="w-6 h-6 text-white" strokeWidth={2.5}/></div>
            )}
            <div>
              <div className="text-white font-bold leading-tight" style={{fontFamily:'Outfit'}}>{co?.company_name || "MRM Renewables"}</div>
              <div className="text-amber-100 text-xs">Customer Complaint Registration · शिकायत दर्ज करें</div>
            </div>
          </div>

          <div className="p-6">
            {done ? (
              <div className="text-center py-6" data-testid="complaint-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3"/>
                <div className="text-xl font-bold text-slate-900" style={{fontFamily:'Outfit'}}>Complaint Registered!</div>
                <div className="text-slate-500 text-sm mt-1">Aapki shikayat darj ho gayi hai. Hamari team jald sampark karegi.</div>
                <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Complaint Number</div>
                  <div className="text-2xl font-bold font-mono text-amber-600">{done}</div>
                  <div className="text-xs text-slate-500 mt-1">Yeh number save kar lein — reference ke liye</div>
                </div>
                {co?.phone && (
                  <a href={`tel:${co.phone}`} className="mt-4 inline-flex items-center gap-2 text-sm text-amber-700 font-semibold">
                    <Phone className="w-4 h-4"/> Call us: {co.phone}
                  </a>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="public-complaint-form">
                <div>
                  <Label className="text-xs font-semibold">Your Name / नाम *</Label>
                  <Input required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="mt-1" data-testid="pub-name-input"/>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Mobile Number / मोबाइल *</Label>
                  <Input required value={form.mobile} onChange={e=>setForm({...form, mobile:e.target.value})} className="mt-1" data-testid="pub-mobile-input" placeholder="10-digit mobile"/>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Complaint Type / समस्या का प्रकार *</Label>
                  <Select value={form.complaint_type} onValueChange={v=>setForm({...form, complaint_type:v})}>
                    <SelectTrigger className="mt-1" data-testid="pub-type-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Describe the Problem / समस्या बताएं *</Label>
                  <Textarea required rows={3} value={form.description} onChange={e=>setForm({...form, description:e.target.value})} className="mt-1" data-testid="pub-description-input" placeholder="e.g. Inverter showing error, generation zero since 2 days..."/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">City / शहर</Label>
                    <Input value={form.city} onChange={e=>setForm({...form, city:e.target.value})} className="mt-1" data-testid="pub-city-input"/>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Address / पता</Label>
                    <Input value={form.address} onChange={e=>setForm({...form, address:e.target.value})} className="mt-1" data-testid="pub-address-input"/>
                  </div>
                </div>
                {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="pub-error">{err}</div>}
                <Button type="submit" disabled={loading || !co} className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="pub-submit-btn">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Submitting...</> : "Register Complaint / शिकायत दर्ज करें"}
                </Button>
              </form>
            )}
          </div>
        </div>
        <div className="text-center text-[11px] text-slate-400 mt-3">
          Powered by {co?.company_name || "MRM Renewables"} · {co?.phone}
        </div>
        <Toaster position="top-center" richColors/>
      </div>
    </div>
  );
}
